var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { FormHelperText, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Box, FormControl, } from "@mui/material";
import { buildFieldValidation } from "../../validators/field-components";
import { getDefaultCellValue, renderMatrixCell, } from "../../utils/MatrixCellRenderers";
import CommentArea from "../common/CommentArea";
import { CellType, MatrixConditionKeyEnum, } from "../../../../types/forms";
import { useElementConditions, useFormLogic, } from "../../context/FormLogicContext";
import Header from "../common/Header";
import { getMatrixColumnsConditionStatus, getMatrixVisibleColumns, computeColumnTotal, formatColumnTotalValue, getCommentFieldName, resolveMatrixExpressionCell, } from "../../../../utils/forms-functions";
import { getElementValueKey } from "../../../../utils/helpers";
import { useClearInvisibleValue } from "../../hooks/useClearInvisibleValue";
var normalizeMatrixDropdownValues = function (raw, visibleColumns, rows, elementCellType) {
    var _a;
    var value = (raw !== null && raw !== void 0 ? raw : {});
    var normalizedData = {};
    for (var _i = 0, rows_1 = rows; _i < rows_1.length; _i++) {
        var row = rows_1[_i];
        var rowValue = typeof row === "string" ? row : row.value;
        var prevRow = ((_a = value[rowValue]) !== null && _a !== void 0 ? _a : {});
        normalizedData[rowValue] = {};
        for (var _b = 0, visibleColumns_1 = visibleColumns; _b < visibleColumns_1.length; _b++) {
            var col = visibleColumns_1[_b];
            var columnCellType = col.cellType || elementCellType;
            var colName = col.name;
            if (Object.prototype.hasOwnProperty.call(prevRow, colName)) {
                normalizedData[rowValue][colName] = prevRow[colName];
            }
            else {
                normalizedData[rowValue][colName] = getDefaultCellValue(columnCellType);
            }
        }
    }
    return normalizedData;
};
var MatrixDropdownField = function (_a) {
    var element = _a.element, errors = _a.errors;
    var _b = useFormLogic(), formValues = _b.formValues, elementsTypeInfoByName = _b.elementsTypeInfoByName, globalConstraints = _b.globalConstraints;
    var _c = useFormContext(), unregister = _c.unregister, getValues = _c.getValues, setValue = _c.setValue;
    var valueKey = getElementValueKey(element);
    var error = errors[valueKey];
    var _d = useElementConditions(element), isVisible = _d.isVisible, isEnabled = _d.isEnabled, isInteractive = _d.isInteractive, fieldRequired = _d.isRequired;
    var valueKeys = useMemo(function () {
        return element.showCommentArea
            ? [valueKey, getCommentFieldName(valueKey)]
            : [valueKey];
    }, [valueKey, element.showCommentArea]);
    useClearInvisibleValue({
        isVisible: isVisible,
        valueKeys: valueKeys,
        unregister: unregister,
    });
    var rows = useMemo(function () {
        return (element.rows || []);
    }, [element.rows]);
    var columns = useMemo(function () {
        return (element.columns || []);
    }, [element.columns]);
    var visibleColumns = useMemo(function () {
        return getMatrixVisibleColumns(columns, formValues, elementsTypeInfoByName);
    }, [columns, formValues, elementsTypeInfoByName]);
    var columnsDisabledByEnableIf = useMemo(function () {
        return getMatrixColumnsConditionStatus({
            visibleColumns: visibleColumns,
            formValues: formValues,
            elementsTypeInfoByName: elementsTypeInfoByName,
            conditionKey: MatrixConditionKeyEnum.ENABLE_IF,
            shouldInvertLogic: true,
        });
    }, [visibleColumns, formValues, elementsTypeInfoByName]);
    var columnsRequiredByRequiredIf = useMemo(function () {
        return getMatrixColumnsConditionStatus({
            visibleColumns: visibleColumns,
            formValues: formValues,
            elementsTypeInfoByName: elementsTypeInfoByName,
            conditionKey: MatrixConditionKeyEnum.REQUIRED_IF,
        });
    }, [visibleColumns, formValues, elementsTypeInfoByName]);
    var validation = useMemo(function () {
        return buildFieldValidation(__assign(__assign({}, element), { columns: visibleColumns }), {
            isRequired: fieldRequired,
            readOnly: !isEnabled,
            matrixColumnDisabledByEnableIf: columnsDisabledByEnableIf,
            matrixColumnRequiredByRequiredIf: columnsRequiredByRequiredIf,
        });
    }, [
        element,
        fieldRequired,
        isEnabled,
        visibleColumns,
        columnsDisabledByEnableIf,
        columnsRequiredByRequiredIf,
    ]);
    var choices = useMemo(function () {
        return (element.choices || []);
    }, [element.choices]);
    var choiceValues = useMemo(function () {
        return choices.map(function (choice) {
            if (typeof choice === "number") {
                return { value: choice.toString(), text: choice.toString() };
            }
            if (typeof choice === "string") {
                return { value: choice, text: choice };
            }
            return { value: choice.value.toString(), text: choice.text };
        });
    }, [choices]);
    var elementCellType = element.cellType || CellType.DEFAULT;
    var rateCount = element.rateCount || 5;
    useEffect(function () {
        if (!isVisible)
            return;
        var current = getValues(valueKey);
        var normalizedData = normalizeMatrixDropdownValues(current, visibleColumns, rows, elementCellType);
        if (JSON.stringify(current !== null && current !== void 0 ? current : null) !== JSON.stringify(normalizedData)) {
            setValue(valueKey, normalizedData);
        }
    }, [
        isVisible,
        valueKey,
        visibleColumns,
        rows,
        elementCellType,
        getValues,
        setValue,
    ]);
    var handleCellChange = function (row, column, value, currentValues, onChange) {
        var _a, _b;
        var newValues = __assign(__assign({}, currentValues), (_a = {}, _a[row] = __assign(__assign({}, (currentValues[row] || {})), (_b = {}, _b[column] = value, _b)), _a));
        onChange(newValues);
    };
    if (!element.rows && element.hideIfRowsEmpty) {
        return null;
    }
    return (_jsx(_Fragment, { children: isVisible ? (_jsx(Box, __assign({ className: "component-wrapper" }, { children: _jsxs(FormControl, __assign({ fullWidth: true, error: !!error, sx: { mb: 3 } }, { children: [_jsx(Header, { element: element, error: !!error, isRequired: fieldRequired }), _jsx(Controller, { name: valueKey, rules: {
                            validate: function (value) {
                                var _a;
                                var result = validation.safeParse(value);
                                if (!result.success) {
                                    return ((_a = result.error.errors[0]) === null || _a === void 0 ? void 0 : _a.message) || "Valor inválido";
                                }
                                return true;
                            },
                        }, render: function (_a) {
                            var _b;
                            var field = _a.field;
                            var currentValues = ((_b = field.value) !== null && _b !== void 0 ? _b : {});
                            return (_jsxs(Box, { children: [_jsx(TableContainer, __assign({ sx: {
                                            boxShadow: "none",
                                            backgroundColor: "transparent",
                                        } }, { children: _jsxs(Table, __assign({ size: "small" }, { children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(TableCell, { sx: {
                                                                    fontWeight: 600,
                                                                } }), visibleColumns.map(function (column) {
                                                                var _a;
                                                                var displayTitle = column.title || column.name;
                                                                var columnIsRequired = column.isRequired ||
                                                                    columnsRequiredByRequiredIf[column.name];
                                                                var requiredText = (_a = globalConstraints.requiredText) !== null && _a !== void 0 ? _a : "*";
                                                                return (_jsxs(TableCell, __assign({ align: "center", sx: {
                                                                        fontWeight: 600,
                                                                        minWidth: 150,
                                                                    } }, { children: [displayTitle, " ", columnIsRequired && requiredText && (_jsx(Box, __assign({ component: "span", sx: { color: "error.main" } }, { children: requiredText })))] }), column.name));
                                                            })] }) }), _jsxs(TableBody, { children: [rows.map(function (row, rowIndex) { return (_jsxs(TableRow, { children: [_jsx(TableCell, __assign({ component: "th", scope: "row", sx: {
                                                                        fontWeight: 500,
                                                                        whiteSpace: "nowrap",
                                                                        maxWidth: 210,
                                                                    }, title: typeof row === "string" ? row : row.text }, { children: typeof row === "string" ? row : row.text })), visibleColumns.map(function (column) {
                                                                    var _a, _b, _c;
                                                                    var columnValue = column.name;
                                                                    var columnCellType = column.cellType || elementCellType;
                                                                    var rowValue = typeof row === "string" ? row : row.value;
                                                                    var cellValue = columnCellType === CellType.EXPRESSION
                                                                        ? resolveMatrixExpressionCell(column, (_a = currentValues === null || currentValues === void 0 ? void 0 : currentValues[rowValue]) !== null && _a !== void 0 ? _a : {}, formValues)
                                                                        : (_c = (_b = currentValues === null || currentValues === void 0 ? void 0 : currentValues[rowValue]) === null || _b === void 0 ? void 0 : _b[columnValue]) !== null && _c !== void 0 ? _c : getDefaultCellValue(columnCellType);
                                                                    var cellDisabled = element.readOnly === true ||
                                                                        !isInteractive ||
                                                                        column.readOnly === true ||
                                                                        Boolean(columnsDisabledByEnableIf[columnValue]);
                                                                    return (_jsx(TableCell, __assign({ align: "center" }, { children: renderMatrixCell({
                                                                            row: rowValue,
                                                                            column: columnValue,
                                                                            cellValue: cellValue,
                                                                            element: element,
                                                                            cellDisabled: cellDisabled,
                                                                            cellType: columnCellType,
                                                                            columnDefinition: column,
                                                                            choiceValues: choiceValues,
                                                                            rateCount: rateCount,
                                                                            autoGrowComment: globalConstraints.autoGrowComment,
                                                                            onCellChange: function (row, column, value) {
                                                                                return handleCellChange(row, column, value, currentValues, field.onChange);
                                                                            },
                                                                            error: error,
                                                                        }) }), columnValue));
                                                                })] }, rowIndex)); }), visibleColumns.some(function (col) { return col.totalType !== undefined; }) && (_jsxs(TableRow, { children: [_jsx(TableCell, { sx: { fontWeight: 600 } }), visibleColumns.map(function (column) {
                                                                    if (!column.totalType) {
                                                                        return _jsx(TableCell, {}, column.name);
                                                                    }
                                                                    var colValues = rows.map(function (row) {
                                                                        var _a;
                                                                        var rowKey = typeof row === "string" ? row : row.value;
                                                                        return (_a = currentValues === null || currentValues === void 0 ? void 0 : currentValues[rowKey]) === null || _a === void 0 ? void 0 : _a[column.name];
                                                                    });
                                                                    var total = computeColumnTotal(colValues, column.totalType);
                                                                    var formatted = formatColumnTotalValue(total, column.totalDisplayStyle, column.totalCurrency);
                                                                    return (_jsx(TableCell, __assign({ align: "right", sx: {
                                                                            fontWeight: 600,
                                                                            borderBottom: "1px solid #e0e0e0 !important",
                                                                        } }, { children: formatted }), column.name));
                                                                })] }))] })] })) })), error && (_jsx(FormHelperText, __assign({ error: true, sx: { mt: 1 } }, { children: error.message }))), element.showCommentArea && (_jsx(CommentArea, { element: element, disabled: !isInteractive }))] }));
                        } })] })) }))) : null }));
};
export default MatrixDropdownField;
